import { AlertTriangle, Download, MousePointerClick, ShieldAlert } from 'lucide-react';
import type { EstadoImpresionVista } from '@shared/types';

/**
 * Sprint 73 — Cómo cambiar a la versión nueva, explicado para quien no es de computadoras.
 * Sprint 74 — Autosuficiente: con su propio botón de descarga.
 *
 * ## Por qué existe
 *
 * Se publicó una versión nueva del programa de la caja y los pasos para instalarla encima de la que
 * ya está **sólo existían en una conversación** con soporte. La pantalla de la dueña no decía nada, y
 * peor: el aviso que había le prometía *"la caja se actualizará sola, no tienes que hacer nada"* —
 * falso para las versiones anteriores a la 1.62.2, que salieron sin el componente de
 * auto-actualización (hotfix v1.62.1). Le prometíamos algo que no iba a pasar.
 *
 * ## El defecto del Sprint 73, y la lección
 *
 * La primera versión de este bloque decía *"descarga la versión nueva con el botón del paso 1 de
 * arriba"*. **Ese botón no existe cuando este bloque se ve.** Vive en el asistente de instalación, y
 * el asistente sólo se dibuja cuando la caja NO está conectada; este bloque, al contrario, sólo
 * aparece cuando SÍ lo está. Nunca coinciden en pantalla.
 *
 * José lo encontró en dos segundos: *"¿y el software para descargar el agente? ¡NO HAY LINK DE
 * DESCARGA!"*. La lección es que **un instructivo no puede referirse a algo que está "arriba"** si no
 * puede verificar que ahí está: o trae la acción adentro, o no la menciona. Por eso ahora el botón de
 * descarga es parte del bloque.
 *
 * ## El criterio
 *
 * El mismo del aviso de SmartScreen de esta pantalla, que es lo mejor que tiene: **se dibuja lo que
 * va a ver**. Un paso por acción, en el orden en que la mano se mueve, y nombrando las cosas como se
 * llaman en su pantalla ("junto al reloj", "clic derecho") en lugar de como se llaman para nosotros
 * ("bandeja del sistema", "terminar el proceso").
 */

interface Props {
  estado: EstadoImpresionVista | null;
  /** Versión publicada en el sitio. */
  versionNueva: string | null;
  /** Peso del instalador, para que sepa qué esperar antes de tocar Descargar. */
  tamanoMb: number | null;
}

/**
 * Las versiones anteriores a la 1.62.2 **no se pueden actualizar solas**: ese paquete salió sin
 * `electron-updater` adentro (hotfix v1.62.1). Es el dato que decide si a la dueña se le pide hacer
 * algo o se le dice que no haga nada — y prometerle lo segundo cuando es lo primero es lo que la
 * dejó esperando.
 */
const PRIMERA_QUE_SE_ACTUALIZA_SOLA = [1, 62, 2] as const;

function esAnteriorA(version: string, referencia: readonly number[]): boolean {
  const partes = version.split('.').map((n) => Number(n) || 0);
  for (let i = 0; i < referencia.length; i++) {
    const a = partes[i] ?? 0;
    const b = referencia[i];
    if (a !== b) return a < b;
  }
  return false;
}

/** El ícono junto al reloj, dibujado. Es el estorbo real al reinstalar y nadie lo relaciona. */
function IconoJuntoAlReloj() {
  return (
    <div className="mt-2 inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-slate-100 px-3 py-1.5 text-xs text-slate-700 shadow-sm">
      <span className="text-slate-400">˄</span>
      <span className="rounded bg-white px-1.5 py-0.5">🖨️</span>
      <MousePointerClick className="h-3.5 w-3.5 text-blue-600" />
      <span className="font-medium">clic derecho → Salir</span>
      <span className="ml-2 border-l border-slate-300 pl-2 font-mono text-slate-500">8:42 p.m.</span>
    </div>
  );
}

export function ComoCambiarVersionCaja({ estado, versionNueva, tamanoMb }: Props) {
  const caja = estado?.agentes?.[0];
  // Sin caja registrada, esto no aplica: los pasos de instalación desde cero ya están en el asistente.
  if (!caja || !versionNueva) return null;
  const versionActual = caja.versionAgente;
  if (!versionActual || versionActual === versionNueva) return null;

  const seActualizaSola = !esAnteriorA(versionActual, PRIMERA_QUE_SE_ACTUALIZA_SOLA);

  return (
    <div className="mt-4 rounded-xl border-2 border-blue-200 bg-blue-50/60 p-4">
      <h3 className="flex items-center gap-2 font-semibold text-blue-950">
        <Download className="h-5 w-5" />
        Hay una versión nueva del programa de la caja
      </h3>
      <p className="mt-1 text-sm text-blue-900">
        Tu caja tiene la <b>{versionActual}</b> y la nueva es la <b>{versionNueva}</b>.
      </p>

      {seActualizaSola ? (
        /*
          A partir de la 1.62.2 el programa se actualiza solo. Aquí SÍ se le puede decir que no haga
          nada — y se le dice cuándo va a pasar, porque "solo" sin un cuándo se siente como "nunca".

          Sprint 74 — Y no se le dan pasos de descarga, que era el otro sinsentido del bloque
          anterior: decía "no tienes que hacer nada" y acto seguido listaba tres pasos con un botón
          de descargar. Si se actualiza solo, lo único que puede querer es que sea AHORA, y para eso
          no se descarga nada: se cierra y se abre.
        */
        <>
          <div className="mt-3 rounded-lg bg-white/80 px-3 py-2 text-sm text-slate-700">
            <p>
              <b>No tienes que hacer nada.</b> El programa se actualiza solo: descarga la versión
              nueva en segundo plano y la aplica la próxima vez que se cierre —normalmente al apagar
              la computadora—.
            </p>
          </div>

          <div className="mt-3 rounded-lg bg-white/80 px-3 py-2 text-sm text-slate-700">
            <p className="font-medium">¿Quieres que sea ahora mismo?</p>
            <p className="mt-1 text-slate-600">
              Nada que descargar: sólo cierra el programa de la caja y vuelve a abrirlo. Busca el
              ícono de la impresora <b>junto al reloj</b>, abajo a la derecha de la pantalla; si no lo
              ves, toca la flechita <b>˄</b> que está ahí mismo.
            </p>
            <IconoJuntoAlReloj />
            <p className="mt-2 text-slate-600">
              Luego ábrelo otra vez desde el <b>menú de inicio</b>, buscando{' '}
              <b>RestaurantPOS Impresión</b>. Se actualiza al arrancar y no te va a pedir el código:
              esta caja ya está conectada.
            </p>
          </div>
        </>
      ) : (
        <>
          <div className="mt-3 flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <p>
              <b>Esta vez hay que cambiarla a mano</b>, una sola vez. La versión que tienes instalada
              no puede actualizarse por su cuenta. Después de este cambio, las siguientes se
              actualizan solas y no tendrás que volver a hacerlo.
            </p>
          </div>

          <ol className="mt-4 space-y-3 text-sm text-slate-800">
            <li>
              <b>1. Cierra el programa de la caja.</b>
              <div className="mt-1 text-slate-600">
                Busca el ícono de la impresora <b>junto al reloj</b>, abajo a la derecha de la
                pantalla. Si no lo ves, toca la flechita <b>˄</b> que está ahí mismo para mostrar los
                escondidos.
              </div>
              <IconoJuntoAlReloj />
              <div className="mt-1 text-xs text-slate-500">
                Si tu programa ya es de las nuevas, ábrelo con doble clic y usa el botón{' '}
                <b>Cerrar el agente (para reinstalar)</b>.
              </div>
            </li>

            <li>
              <b>2. Descarga la versión nueva y ábrela con doble clic.</b>
              {/*
                Sprint 74 — El botón vive AQUÍ.

                Antes este paso decía "con el botón del paso 1 de arriba", y ese botón pertenece al
                asistente de instalación, que no se dibuja cuando la caja está conectada — o sea,
                nunca cuando este bloque se ve. Un instructivo no puede mandar a un botón que no
                puede verificar; trae la acción adentro.
              */}
              <a
                href="/api/agente/instalador"
                className="btn-primary mt-2 inline-flex min-h-[44px] items-center gap-2"
              >
                <Download className="h-5 w-5" />
                Descargar versión {versionNueva}
                {tamanoMb ? ` (${tamanoMb} MB)` : ''}
              </a>
              <div className="mt-2 flex items-start gap-2 text-slate-600">
                <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                <span>
                  Windows va a avisar que no reconoce el programa. Es normal y ya lo has visto: toca{' '}
                  <b>Más información</b> y luego <b>Ejecutar de todas formas</b>.
                </span>
              </div>
            </li>

            <li>
              <b>3. Espera a que termine y ya.</b>
              <div className="mt-1 text-slate-600">
                El programa se abre solo. <b>No te va a pedir el código otra vez</b>: esta caja ya
                está conectada.
              </div>
              {/*
                Sprint 74 — El plan B, dicho de forma verificable.

                Antes mandaba a "copiar el código del paso 3 de arriba", que tampoco existe aquí. Lo
                que sí es cierto y se puede afirmar: la caja se marca desconectada tras dos minutos
                sin señal (`MINUTOS_SIN_CONTACTO_DESCONECTADO`), y entonces esta misma pantalla
                cambia sola al asistente, que trae el botón para generar un código nuevo.
              */}
              <div className="mt-1 text-xs text-slate-500">
                Si por alguna razón te pide un código, espera un par de minutos con el programa
                cerrado y vuelve a esta pantalla: aparecerá sola el botón para generar uno nuevo.
              </div>
            </li>
          </ol>
        </>
      )}

      <p className="mt-3 rounded-lg bg-white/80 px-3 py-2 text-xs text-slate-600">
        Mientras el programa esté cerrado no salen tickets ni comandas por la impresora. Son un par de
        minutos: si estás en plena venta, hazlo al cerrar el día.
      </p>
    </div>
  );
}
